import React, { useState, useEffect } from 'react';import API_BASE_URL from '../config';import styles from '../css-isolated/sa_auditlogin.module.css';
import authFetch from '../utils/authFetch';

function SaAuditLogin({ isDarkMode }) {
  const [auditLogs, setAuditLogs] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [filterUserType, setFilterUserType] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [dateFilter, setDateFilter] = useState('all');
  const [specificDate, setSpecificDate] = useState('');
  const [dateRangeFrom, setDateRangeFrom] = useState('');
  const [dateRangeTo, setDateRangeTo] = useState('');

  // Date modal state
  const [isDateModalOpen, setIsDateModalOpen] = useState(false);
  const [modalMonth, setModalMonth] = useState('');
  const [modalDay, setModalDay] = useState('');
  const [modalYear, setModalYear] = useState('');
  const [modalTextDate, setModalTextDate] = useState('');
  const [modalError, setModalError] = useState('');
  const [modalType, setModalType] = useState(''); // 'specific', 'from', or 'to'

  const ITEMS_PER_PAGE = 10;

  // Helper function to get days in month
  const getDaysInMonth = (month, year) => {
    if (!month) return 31;
    const monthNum = parseInt(month);
    if (monthNum === 2) {
      if (!year) return 29;
      const yearNum = parseInt(year);
      return (yearNum % 4 === 0 && yearNum % 100 !== 0) || (yearNum % 400 === 0) ? 29 : 28;
    }
    if ([4, 6, 9, 11].includes(monthNum)) return 30;
    return 31;
  };

  // Fetch audit logs
  useEffect(() => {
    fetchAuditLogs();
  }, []);

  const fetchAuditLogs = async () => {
    setIsLoading(true);
    try {
      const response = await authFetch(`${API_BASE_URL}/api/audit/login-logs`);
      if (!response.ok) {
        throw new Error('Failed to fetch audit logs');
      }
      const data = await response.json();
      setAuditLogs(data);
    } catch (error) {
      console.error('Error fetching audit logs:', error);
      setAuditLogs([]);
    } finally {
      setIsLoading(false);
    }
  };

  // Filter logs
  const filteredLogs = React.useMemo(() => {
    let filtered = auditLogs;

    // Filter by user type
    if (filterUserType !== 'all') {
      filtered = filtered.filter(log => log.user_type === filterUserType);
    }

    // Filter by search query
    if (searchQuery) {
      filtered = filtered.filter(log => 
        (log.username && log.username.toLowerCase().includes(searchQuery.toLowerCase())) ||
        (log.email && log.email.toLowerCase().includes(searchQuery.toLowerCase()))
      );
    }

    // Filter by date
    if (dateFilter !== 'all') {
      const now = new Date();
      filtered = filtered.filter(log => {
        const logDate = new Date(log.login_time);
        switch (dateFilter) {
          case 'today':
            return logDate.toDateString() === now.toDateString();
          case 'week':
            const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
            return logDate >= weekAgo;
          case 'month':
            const monthAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
            return logDate >= monthAgo;
          case 'specific':
            if (specificDate) {
              const selectedDate = new Date(specificDate);
              return logDate.toDateString() === selectedDate.toDateString();
            }
            return true;
          case 'range':
            if (dateRangeFrom && dateRangeTo) {
              const fromDate = new Date(dateRangeFrom);
              const toDate = new Date(dateRangeTo);
              toDate.setHours(23, 59, 59, 999); // Include the entire end date
              return logDate >= fromDate && logDate <= toDate;
            } else if (dateRangeFrom) {
              const fromDate = new Date(dateRangeFrom);
              return logDate >= fromDate;
            } else if (dateRangeTo) {
              const toDate = new Date(dateRangeTo);
              toDate.setHours(23, 59, 59, 999);
              return logDate <= toDate;
            }
            return true;
          default:
            return true;
        }
      });
    }

    return filtered;
  }, [auditLogs, filterUserType, searchQuery, dateFilter, specificDate, dateRangeFrom, dateRangeTo]);

  // Pagination
  const totalPages = Math.ceil(filteredLogs.length / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const endIndex = startIndex + ITEMS_PER_PAGE;
  const currentPageData = filteredLogs.slice(startIndex, endIndex);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [filterUserType, searchQuery, dateFilter, specificDate, dateRangeFrom, dateRangeTo]);

  const formatDateTime = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  const getUserTypeLabel = (userType) => {
    const labels = {
      'superuser': 'Super User',
      'admin': 'Admin',
      'unit_nurse': 'Unit Nurse',
      'chief_nurse': 'Chief Nurse',
      'jail_officer': 'Jail Officer'
    };
    return labels[userType] || userType;
  };

  const getUserTypeBadgeClass = (userType) => {
    const classes = {
      'superuser': styles.badgeSuperuser,
      'admin': styles.badgeAdmin,
      'unit_nurse': styles.badgeUnitNurse,
      'chief_nurse': styles.badgeChiefNurse,
      'jail_officer': styles.badgeJailOfficer
    };
    return classes[userType] || styles.badgeDefault;
  };

  // Format username: Last, First M.
  const formatDisplayName = (log) => {
    const pick = (obj, keys) => {
      if (!obj) return '';
      for (const k of keys) {
        const v = obj[k];
        if (typeof v === 'string' && v.trim()) return v.trim();
      }
      return '';
    };
    const ln = pick(log, ['last_name','lastName']) || pick(log.account, ['last_name','lastName']) || pick(log.user, ['last_name','lastName']);
    const fn = pick(log, ['first_name','firstName']) || pick(log.account, ['first_name','firstName']) || pick(log.user, ['first_name','firstName']);
    const mn = pick(log, ['middle_name','middleName']) || pick(log.account, ['middle_name','middleName']) || pick(log.user, ['middle_name','middleName']);

    const cap = (s) => s ? s.charAt(0).toUpperCase() + s.slice(1).toLowerCase() : '';
    const last = cap(ln);
    const first = cap(fn);
    const middleInitial = mn ? `${mn.charAt(0).toUpperCase()}.` : '';

    if (last || first || middleInitial) {
      const part1 = last;
      const sep = last && (first || middleInitial) ? ', ' : '';
      const part2 = first;
      const space = first && middleInitial ? ' ' : '';
      const formatted = `${part1}${sep}${part2}${space}${middleInitial}`.trim();
      return formatted || 'N/A';
    }
    // Fallback: parse log.username like "first middle last" or "first last"
    const uname = (log.username || '').toString().trim();
    if (!uname) return 'N/A';
    const parts = uname.split(/\s+/).filter(Boolean);
    let uFirst = '';
    let uMiddle = '';
    let uLast = '';
    if (parts.length === 1) {
      // Single token: treat as first name
      uFirst = parts[0];
    } else if (parts.length === 2) {
      // Two tokens: first last
      [uFirst, uLast] = parts;
    } else {
      // Three or more: first, middle, last (use last token as last name)
      uFirst = parts[0];
      uLast = parts[parts.length - 1];
      uMiddle = parts.slice(1, -1).join(' ');
    }
    const uLastCap = cap(uLast);
    const uFirstCap = cap(uFirst);
    const uMiddleInitial = uMiddle ? `${uMiddle.charAt(0).toUpperCase()}.` : '';
    const sep2 = uLastCap && (uFirstCap || uMiddleInitial) ? ', ' : '';
    const space2 = uFirstCap && uMiddleInitial ? ' ' : '';
    const formattedU = `${uLastCap}${sep2}${uFirstCap}${space2}${uMiddleInitial}`.trim();
    return formattedU || uname;
  };

  return (
    <div className={`${styles.auditContainer} ${isDarkMode ? styles.darkMode : styles.lightMode}`}>
      {/* Date Modal */}
      {isDateModalOpen && (
        <div className={`${styles.modalOverlay} ${!isDarkMode ? 'lightMode' : ''}`} onClick={() => setIsDateModalOpen(false)}>
          <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h3>Select Date</h3>
            </div>
            <div className={styles.modalBody}>
              <div className={styles.modalRow}>
                <div className={styles.modalField}>
                  <label>Month</label>
                  <select value={modalMonth} onChange={(e) => {
                    const newMonth = e.target.value;
                    setModalMonth(newMonth);
                    if (newMonth && modalDay && modalYear) {
                      setModalTextDate(`${newMonth}/${modalDay}/${modalYear}`);
                    }
                    if (modalDay) {
                      const maxDays = getDaysInMonth(newMonth, modalYear);
                      if (parseInt(modalDay) > maxDays) {
                        setModalDay('');
                        setModalTextDate('');
                      }
                    }
                  }}>
                    <option value="">Select month</option>
                    {[
                      { label: 'January', value: '01' },
                      { label: 'February', value: '02' },
                      { label: 'March', value: '03' },
                      { label: 'April', value: '04' },
                      { label: 'May', value: '05' },
                      { label: 'June', value: '06' },
                      { label: 'July', value: '07' },
                      { label: 'August', value: '08' },
                      { label: 'September', value: '09' },
                      { label: 'October', value: '10' },
                      { label: 'November', value: '11' },
                      { label: 'December', value: '12' }
                    ].map(m => (
                      <option key={m.value} value={m.value}>{m.label}</option>
                    ))}
                  </select>
                </div>
                <div className={styles.modalField}>
                  <label>Day</label>
                  <select value={modalDay} onChange={(e) => {
                    const newDay = e.target.value;
                    setModalDay(newDay);
                    if (modalMonth && newDay && modalYear) {
                      setModalTextDate(`${modalMonth}/${newDay}/${modalYear}`);
                    }
                  }}>
                    <option value="">Select day</option>
                    {Array.from({length: getDaysInMonth(modalMonth, modalYear)}, (_,i)=>String(i+1).padStart(2,'0')).map(d => (
                      <option key={d} value={d}>{d}</option>
                    ))}
                  </select>
                </div>
                <div className={styles.modalField}>
                  <label>Year</label>
                  <select value={modalYear} onChange={(e) => {
                    const newYear = e.target.value;
                    setModalYear(newYear);
                    if (modalMonth && modalDay && newYear) {
                      setModalTextDate(`${modalMonth}/${modalDay}/${newYear}`);
                    }
                    if (modalDay && modalMonth) {
                      const maxDays = getDaysInMonth(modalMonth, newYear);
                      if (parseInt(modalDay) > maxDays) {
                        setModalDay('');
                        setModalTextDate('');
                      }
                    }
                  }}>
                    <option value="">Select year</option>
                    {Array.from({length: 50}, (_,i)=>String(new Date().getFullYear()-i)).map(y => (
                      <option key={y} value={y}>{y}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className={styles.modalRow}>
                <div className={styles.modalField} style={{flex:1}}>
                  <label>Type date (mm/dd/yyyy)</label>
                  <input
                    type="text"
                    placeholder="mm/dd/yyyy"
                    value={modalTextDate}
                    onChange={(e) => {
                      const val = e.target.value;
                      if (val.length < modalTextDate.length) {
                        setModalTextDate(val);
                        return;
                      }
                      if (/^[\d/]*$/.test(val)) {
                        let formatted = val.replace(/\//g, '');
                        if (formatted.length >= 1) {
                          const firstDigit = parseInt(formatted[0]);
                          if (firstDigit > 1) {
                            formatted = '0' + formatted;
                          }
                        }
                        if (formatted.length >= 2) {
                          const month = parseInt(formatted.slice(0, 2));
                          if (month > 12 || month === 0) {
                            return;
                          }
                          formatted = formatted.slice(0, 2) + '/' + formatted.slice(2);
                        }
                        if (formatted.length >= 4) {
                          const dayPart = formatted.slice(3).replace('/', '');
                          if (dayPart.length >= 1) {
                            const firstDigit = parseInt(dayPart[0]);
                            if (firstDigit > 3) {
                              return;
                            }
                          }
                          formatted = formatted.slice(0, 5) + '/' + formatted.slice(5);
                        }
                        if (formatted.length >= 5) {
                          const parts = formatted.split('/');
                          if (parts.length >= 2) {
                            const month = parts[0];
                            const day = parts[1];
                            const year = parts[2] || '';
                            if (day.length === 2) {
                              const dayNum = parseInt(day);
                              const maxDays = getDaysInMonth(month, year || modalYear || new Date().getFullYear().toString());
                              if (dayNum > maxDays || dayNum === 0) {
                                return;
                              }
                            }
                            if (year.length === 4) {
                              const yearNum = parseInt(year);
                              if (yearNum > 2025) {
                                return;
                              }
                            }
                          }
                        }
                        if (formatted.length <= 10) {
                          setModalTextDate(formatted);
                          if (formatted.length === 10) {
                            const [mm, dd, yyyy] = formatted.split('/');
                            if (mm && dd && yyyy) {
                              setModalMonth(mm);
                              setModalDay(dd);
                              setModalYear(yyyy);
                            }
                          }
                        }
                      }
                    }}
                    maxLength="10"
                  />
                </div>
              </div>
              {modalError && (
                <div className={styles.modalError}>{modalError}</div>
              )}
              <div className={styles.modalHelper}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/>
                  <path d="M12 16v-4M12 8h.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                </svg>
                <span>Select a date using the dropdowns above or type it in mm/dd/yyyy format</span>
              </div>
            </div>
            <div className={styles.modalFooter}>
              <button className={styles.modalBtnGhost} onClick={() => setIsDateModalOpen(false)}>Cancel</button>
              <button
                className={styles.modalBtn}
                onClick={() => {
                  let final = '';
                  setModalError('');
                  if (modalTextDate) {
                    const m = /^\d{1,2}\/\d{1,2}\/\d{4}$/.test(modalTextDate);
                    if (m) {
                      let [mm,dd,yyyy] = modalTextDate.split('/');
                      mm = mm.padStart(2, '0');
                      dd = dd.padStart(2, '0');
                      const monthNum = parseInt(mm);
                      const dayNum = parseInt(dd);
                      const yearNum = parseInt(yyyy);
                      if (monthNum < 1 || monthNum > 12) {
                        setModalError('Invalid month. Please enter a month between 01 and 12.');
                        return;
                      }
                      const maxDays = getDaysInMonth(mm, yyyy);
                      if (dayNum < 1 || dayNum > maxDays) {
                        if (monthNum === 2 && dayNum === 29) {
                          setModalError(`February ${dayNum} is not valid for ${yearNum}. ${yearNum} is not a leap year.`);
                        } else {
                          setModalError(`Invalid day. ${monthNum === 2 ? 'February' : 'This month'} only has ${maxDays} days${monthNum === 2 ? ` in ${yearNum}` : ''}.`);
                        }
                        return;
                      }
                      final = `${yyyy}-${mm}-${dd}`;
                    } else {
                      setModalError('Please enter a valid date in mm/dd/yyyy format.');
                    }
                  } else if (modalMonth && modalDay && modalYear) {
                    const maxDays = getDaysInMonth(modalMonth, modalYear);
                    const dayNum = parseInt(modalDay);
                    if (dayNum > maxDays) {
                      const monthNum = parseInt(modalMonth);
                      if (monthNum === 2 && dayNum === 29) {
                        setModalError(`February ${dayNum} is not valid for ${modalYear}. ${modalYear} is not a leap year.`);
                      } else {
                        setModalError(`Invalid day selected.`);
                      }
                      return;
                    }
                    final = `${modalYear}-${modalMonth}-${modalDay}`;
                  } else {
                    setModalError('Please select month, day, and year.');
                  }
                  if (final) {
                    if (modalType === 'specific') {
                      setSpecificDate(final);
                    } else if (modalType === 'from') {
                      setDateRangeFrom(final);
                    } else if (modalType === 'to') {
                      setDateRangeTo(final);
                    }
                    setIsDateModalOpen(false);
                    setModalMonth(''); setModalDay(''); setModalYear(''); setModalTextDate(''); setModalType('');
                  }
                }}
                disabled={!(modalTextDate || (modalMonth && modalDay && modalYear))}
              >Apply</button>
            </div>
          </div>
        </div>
      )}

      {/* Filters Section */}
      <div className={styles.filtersCard}>
        <div className={styles.filterRow}>
          <div className={styles.filterGroup}>
            <label className={styles.filterLabel}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" stroke="currentColor" strokeWidth="2"/>
                <circle cx="12" cy="7" r="4" stroke="currentColor" strokeWidth="2"/>
              </svg>
              User Type
            </label>
            <select 
              className={styles.filterSelect}
              value={filterUserType}
              onChange={(e) => setFilterUserType(e.target.value)}
            >
              <option value="all">All Users</option>
              <option value="superuser">Super User</option>
              <option value="admin">Admin</option>
              <option value="chief_nurse">Chief Nurse</option>
              <option value="unit_nurse">Unit Nurse</option>
              <option value="jail_officer">Jail Officer</option>
            </select>
          </div>

          <div className={styles.filterGroup}>
            <label className={styles.filterLabel}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                <rect x="3" y="4" width="18" height="18" rx="2" ry="2" stroke="currentColor" strokeWidth="2"/>
                <line x1="16" y1="2" x2="16" y2="6" stroke="currentColor" strokeWidth="2"/>
                <line x1="8" y1="2" x2="8" y2="6" stroke="currentColor" strokeWidth="2"/>
                <line x1="3" y1="10" x2="21" y2="10" stroke="currentColor" strokeWidth="2"/>
              </svg>
              Date Range
            </label>
            <select 
              className={styles.filterSelect}
              value={dateFilter}
              onChange={(e) => setDateFilter(e.target.value)}
            >
              <option value="all">All Time</option>
              <option value="today">Today</option>
              <option value="week">Last 7 Days</option>
              <option value="month">Last 30 Days</option>
              <option value="specific">Specific Date</option>
              <option value="range">Date Range</option>
            </select>
          </div>

          {/* Specific Date Input */}
          {dateFilter === 'specific' && (
            <div className={styles.filterGroup}>
              <label className={styles.filterLabel}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                  <rect x="3" y="4" width="18" height="18" rx="2" ry="2" stroke="currentColor" strokeWidth="2"/>
                  <line x1="16" y1="2" x2="16" y2="6" stroke="currentColor" strokeWidth="2"/>
                  <line x1="8" y1="2" x2="8" y2="6" stroke="currentColor" strokeWidth="2"/>
                </svg>
                Select Date
              </label>
              <input
                type="text"
                readOnly
                className={styles.dateInput}
                value={specificDate ? (() => {
                  const [yyyy, mm, dd] = specificDate.split('-');
                  return `${mm}/${dd}/${yyyy}`;
                })() : ''}
                placeholder="mm/dd/yyyy"
                onClick={() => {
                  if (specificDate) {
                    const [yyyy, mm, dd] = specificDate.split('-');
                    setModalMonth(mm);
                    setModalDay(dd);
                    setModalYear(yyyy);
                    setModalTextDate(`${mm}/${dd}/${yyyy}`);
                  }
                  setModalType('specific');
                  setIsDateModalOpen(true);
                }}
              />
            </div>
          )}

          {/* Date Range Inputs */}
          {dateFilter === 'range' && (
            <>
              <div className={styles.filterGroup}>
                <label className={styles.filterLabel}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                    <rect x="3" y="4" width="18" height="18" rx="2" ry="2" stroke="currentColor" strokeWidth="2"/>
                    <line x1="16" y1="2" x2="16" y2="6" stroke="currentColor" strokeWidth="2"/>
                    <line x1="8" y1="2" x2="8" y2="6" stroke="currentColor" strokeWidth="2"/>
                  </svg>
                  From Date
                </label>
                <input
                  type="text"
                  readOnly
                  className={styles.dateInput}
                  value={dateRangeFrom ? (() => {
                    const [yyyy, mm, dd] = dateRangeFrom.split('-');
                    return `${mm}/${dd}/${yyyy}`;
                  })() : ''}
                  placeholder="mm/dd/yyyy"
                  onClick={() => {
                    if (dateRangeFrom) {
                      const [yyyy, mm, dd] = dateRangeFrom.split('-');
                      setModalMonth(mm);
                      setModalDay(dd);
                      setModalYear(yyyy);
                      setModalTextDate(`${mm}/${dd}/${yyyy}`);
                    }
                    setModalType('from');
                    setIsDateModalOpen(true);
                  }}
                />
              </div>
              <div className={styles.filterGroup}>
                <label className={styles.filterLabel}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                    <rect x="3" y="4" width="18" height="18" rx="2" ry="2" stroke="currentColor" strokeWidth="2"/>
                    <line x1="16" y1="2" x2="16" y2="6" stroke="currentColor" strokeWidth="2"/>
                    <line x1="8" y1="2" x2="8" y2="6" stroke="currentColor" strokeWidth="2"/>
                  </svg>
                  To Date
                </label>
                <input
                  type="text"
                  readOnly
                  className={styles.dateInput}
                  value={dateRangeTo ? (() => {
                    const [yyyy, mm, dd] = dateRangeTo.split('-');
                    return `${mm}/${dd}/${yyyy}`;
                  })() : ''}
                  placeholder="mm/dd/yyyy"
                  onClick={() => {
                    if (dateRangeTo) {
                      const [yyyy, mm, dd] = dateRangeTo.split('-');
                      setModalMonth(mm);
                      setModalDay(dd);
                      setModalYear(yyyy);
                      setModalTextDate(`${mm}/${dd}/${yyyy}`);
                    }
                    setModalType('to');
                    setIsDateModalOpen(true);
                  }}
                />
              </div>
            </>
          )}

          <div className={styles.filterGroup}>
            <label className={styles.filterLabel}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                <circle cx="11" cy="11" r="8" stroke="currentColor" strokeWidth="2"/>
                <path d="M21 21l-4.35-4.35" stroke="currentColor" strokeWidth="2"/>
              </svg>
              Search
            </label>
            <input
              type="text"
              className={styles.searchInput}
              placeholder="Search by username or email..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>

          <button className={styles.refreshBtn} onClick={fetchAuditLogs}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
              <path d="M23 4v6h-6M1 20v-6h6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            Refresh
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className={styles.statsGrid}>
        <div className={styles.statCard}>
          <div className={styles.statIcon} style={{ background: 'rgba(59, 130, 246, 0.1)', color: '#3b82f6' }}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" stroke="currentColor" strokeWidth="2"/>
              <circle cx="9" cy="7" r="4" stroke="currentColor" strokeWidth="2"/>
              <path d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" stroke="currentColor" strokeWidth="2"/>
            </svg>
          </div>
          <div className={styles.statInfo}>
            <div className={styles.statValue}>{filteredLogs.length}</div>
            <div className={styles.statLabel}>Total Logins</div>
          </div>
        </div>

        <div className={styles.statCard}>
          <div className={styles.statIcon} style={{ background: 'rgba(34, 197, 94, 0.1)', color: '#22c55e' }}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path d="M22 12h-4l-3 9L9 3l-3 9H2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </div>
          <div className={styles.statInfo}>
            <div className={styles.statValue}>{filteredLogs.filter(log => log.status === 'success').length}</div>
            <div className={styles.statLabel}>Successful</div>
          </div>
        </div>

        <div className={styles.statCard}>
          <div className={styles.statIcon} style={{ background: 'rgba(239, 68, 68, 0.1)', color: '#ef4444' }}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/>
              <line x1="15" y1="9" x2="9" y2="15" stroke="currentColor" strokeWidth="2"/>
              <line x1="9" y1="9" x2="15" y2="15" stroke="currentColor" strokeWidth="2"/>
            </svg>
          </div>
          <div className={styles.statInfo}>
            <div className={styles.statValue}>{filteredLogs.filter(log => log.status === 'failed').length}</div>
            <div className={styles.statLabel}>Failed</div>
          </div>
        </div>

        <div className={styles.statCard}>
          <div className={styles.statIcon} style={{ background: 'rgba(168, 85, 247, 0.1)', color: '#a855f7' }}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/>
              <polyline points="12 6 12 12 16 14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </div>
          <div className={styles.statInfo}>
            <div className={styles.statValue}>{new Set(filteredLogs.map(log => log.user_id)).size}</div>
            <div className={styles.statLabel}>Unique Users</div>
          </div>
        </div>
      </div>

      {/* Audit Logs Table */}
      <div className={styles.tableCard}>
        <div className={styles.tableHeader}>
          <h3>Login Activity Logs</h3>
          <span className={styles.recordCount}>
            Showing {startIndex + 1}-{Math.min(endIndex, filteredLogs.length)} of {filteredLogs.length}
          </span>
        </div>

        {isLoading ? (
          <div className={styles.loadingState}>
            <div className={styles.spinner}></div>
            <p>Loading audit logs...</p>
          </div>
        ) : filteredLogs.length === 0 ? (
          <div className={styles.emptyState}>
            <svg width="64" height="64" viewBox="0 0 24 24" fill="none">
              <path d="M9 11H15M9 15H15M17 21H7C5.89543 21 5 20.1046 5 19V5C5 3.89543 5.89543 3 7 3H12.5858C12.851 3 13.1054 3.10536 13.2929 3.29289L19.7071 9.70711C19.8946 9.89464 20 10.149 20 10.4142V19C20 20.1046 19.1046 21 18 21H17Z" stroke="currentColor" strokeWidth="2"/>
            </svg>
            <h4>No Audit Logs Found</h4>
            <p>No login activity matches your current filters.</p>
          </div>
        ) : (
          <>
            <div className={styles.tableWrapper}>
              <table className={styles.auditTable}>
                <thead>
                  <tr>
                    <th>Timestamp</th>
                    <th>Username</th>
                    <th>User Type</th>
                    <th>Email</th>
                    <th>Status</th>
                    <th>Activity</th>
                  </tr>
                </thead>
                <tbody>
                  {currentPageData.map((log, index) => (
                    <tr key={log.id || index}>
                      <td className={styles.timestampCell}>
                        {formatDateTime(log.login_time || log.created_at)}
                      </td>
                      <td className={styles.userCell}>
                        <div className={styles.userInfo}>
                          <span>{formatDisplayName(log)}</span>
                        </div>
                      </td>
                      <td>
                        <span className={`${styles.badge} ${getUserTypeBadgeClass(log.user_type)}`}>
                          {getUserTypeLabel(log.user_type)}
                        </span>
                      </td>
                      <td className={styles.emailCell}>{log.email || 'N/A'}</td>
                      <td>
                        <span className={`${styles.statusBadge} ${log.status === 'success' ? styles.statusSuccess : styles.statusFailed}`}>
                          {log.status === 'success' ? 'Success' : 'Failed'}
                        </span>
                      </td>
                      <td className={styles.deviceCell}>{log.user_agent || 'N/A'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className={styles.pagination}>
                <button
                  className={styles.paginationBtn}
                  onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                  disabled={currentPage === 1}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                    <path d="M15 18L9 12L15 6" stroke="currentColor" strokeWidth="2"/>
                  </svg>
                  Previous
                </button>

                <div className={styles.pageInfo}>
                  Page <strong>{currentPage}</strong> of <strong>{totalPages}</strong>
                </div>

                <button
                  className={styles.paginationBtn}
                  onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                  disabled={currentPage === totalPages}
                >
                  Next
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                    <path d="M9 18L15 12L9 6" stroke="currentColor" strokeWidth="2"/>
                  </svg>
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default SaAuditLogin;
